import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  Check,
  AlertCircle,
  Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const ImportPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({
    articleNumberColumn: 0,
    priceNetColumn: null,
    packageSizeColumn: null,
    skipFirstRow: true
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Lieferanten");
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const res = await axios.post(`${API}/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(res.data);
    } catch (err) {
      toast.error("Fehler beim Lesen der Datei");
    }
  };

  const handleImport = async () => {
    if (!selectedSupplier) {
      toast.error("Bitte Lieferant auswählen");
      return;
    }
    if (!file) {
      toast.error("Bitte CSV-Datei auswählen");
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('supplier_id', selectedSupplier);
      formData.append('article_number_column', mapping.articleNumberColumn);
      
      if (mapping.priceNetColumn !== null) {
        formData.append('price_net_column', mapping.priceNetColumn);
      }
      if (mapping.packageSizeColumn !== null) {
        formData.append('package_size_column', mapping.packageSizeColumn);
      }
      formData.append('skip_first_row', mapping.skipFirstRow);

      const res = await axios.post(`${API}/import/prices`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(res.data);
      
      if (res.data.updated > 0) {
        toast.success(`${res.data.updated} Preise aktualisiert`);
      } else {
        toast.warning("Keine Preise aktualisiert");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Import");
    } finally {
      setImporting(false);
    }
  };

  const ColumnSelect = ({ value, onChange, label }) => (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <Select
        value={value === null ? "none" : String(value)}
        onValueChange={(v) => onChange(v === "none" ? null : parseInt(v))}
      >
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Nicht zuordnen" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Nicht zuordnen</SelectItem>
          {preview?.headers?.map((header, idx) => (
            <SelectItem key={idx} value={String(idx)}>
              Spalte {idx + 1}: {header || `(leer)`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Preisimport
        </h1>
        <p className="text-slate-500 mt-1">
          Einkaufspreise aus CSV-Datei aktualisieren
        </p>
      </div>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-6 h-6 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">So funktioniert der Import:</p>
              <ol className="list-decimal ml-4 mt-1 space-y-1 text-blue-700">
                <li>Lieferant auswählen</li>
                <li>CSV-Datei mit Artikelnummern und Preisen hochladen</li>
                <li>Spalten zuordnen</li>
                <li>Import starten - Preise werden automatisch aktualisiert</li>
              </ol>
              <p className="mt-2 text-xs">
                Hinweis: Nur Produkte mit hinterlegter Artikelnummer werden aktualisiert. Brutto wird aus Netto + Produkt-MwSt berechnet.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Form */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            CSV Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Supplier Selection */}
          <div className="space-y-2">
            <Label>Lieferant *</Label>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger>
                <SelectValue placeholder="Lieferant wählen" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {s.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>CSV-Datei *</Label>
            <Input
              type="file"
              accept=".csv,.txt"
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            <p className="text-xs text-slate-500">
              Format: CSV mit Semikolon (;) als Trennzeichen
            </p>
          </div>

          {/* Preview & Mapping */}
          {preview && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <p className="text-sm font-medium">
                    Vorschau ({preview.total_rows} Zeilen)
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        {preview.headers?.map((header, idx) => (
                          <th key={idx} className="px-3 py-2 text-left font-medium">
                            <span className="text-xs text-slate-400">#{idx + 1}</span>
                            <br />
                            {header || "(leer)"}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows?.slice(0, 3).map((row, rowIdx) => (
                        <tr key={rowIdx} className="border-t">
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx} className="px-3 py-2 truncate max-w-[150px]">
                              {cell || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Column Mapping */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <ColumnSelect
                  value={mapping.articleNumberColumn}
                  onChange={(v) => setMapping({ ...mapping, articleNumberColumn: v ?? 0 })}
                  label="Artikelnummer *"
                />
                <ColumnSelect
                  value={mapping.priceNetColumn}
                  onChange={(v) => setMapping({ ...mapping, priceNetColumn: v })}
                  label="VPE-Preis Netto"
                />
                <ColumnSelect
                  value={mapping.packageSizeColumn}
                  onChange={(v) => setMapping({ ...mapping, packageSizeColumn: v })}
                  label="VPE-Größe"
                />
              </div>

              {/* Options */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="skipFirst"
                  checked={mapping.skipFirstRow}
                  onCheckedChange={(checked) => 
                    setMapping({ ...mapping, skipFirstRow: !!checked })
                  }
                />
                <label htmlFor="skipFirst" className="text-sm">
                  Erste Zeile überspringen (Überschrift)
                </label>
              </div>

              {/* Import Button */}
              <Button
                onClick={handleImport}
                disabled={importing || !selectedSupplier}
                className="w-full bg-rose-600 hover:bg-rose-700"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Preise importieren
                  </>
                )}
              </Button>
            </motion.div>
          )}

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <Check className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-800">
                    Import abgeschlossen
                  </p>
                  <p className="text-sm text-emerald-700">
                    {result.updated} Preise aktualisiert
                  </p>
                </div>
              </div>

              {result.not_found_count > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">
                      {result.not_found_count} Artikelnummern nicht gefunden
                    </span>
                  </div>
                  {result.not_found_articles?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {result.not_found_articles.map((art, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {art}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportPage;
